import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAppContext } from "../AppProviders";
import {
  getReportLogsMock,
  getReportProductReportsMock,
  getReportsInfoListMock,
} from "../__mocks__/reportsList";
import { API_URL, isMocksEnabled } from "./helpers";
import {
  ReportListInputApi,
  ReportsPendingReviewResponse,
  ReportsPendingReviewRequest,
  CreateReportNoteRequest,
  CreateReportInputApi,
  ReportSummaryApi,
  ReportSummaryItemApi,
  WORKFLOW_REPORT_STATUS_ENUM,
  ReportLog,
  ReportRerunSummary,
  CreateReportInputV2Api,
  ReportsAdminCompletedInput,
  LegacyReportResponse,
  LegacyReportRequest,
  ReportsAdminOperationsInput,
  CreateReportInputV2025Api,
  LegacySearchResponse,
  CreateImageRequest,
  GetImagePresignedUrlRequest,
  ReportsByStepNameParams,
  ProductReport,
  MarkReviewedInput,
  CreateActorTimerRequest,
} from "./Reports";
import { getAlignedReportReruns } from "../reports/reportRerunUtils";
import { resolveAndDownload } from "../utils";
import { CacheKeys } from "./Cache";
import { useActorInfoQuery, useGetActorInfo } from "./UsersApi";
import { ActorInfoApi } from "./Users";
import { useFamaFlags } from "./FamaFlags";
import type { RequestApi } from "./index";
import type { ItemWithMetaData } from "./Items";
import {
  WorkflowRun,
  WorkflowStepRun,
  uiNameToWorkflowStepNames,
  WorkflowByStepSummaryItem,
  ReportSummaryPagination,
} from "./Workflows";
import { FilterOptions } from "./Consent";
import { KeyValue } from "../reports/utils";
import { PersonInfoApi } from "./Person";

const PENDING_REVIEW_QUERY_KEY = "reportsPendingReview";

function transformCheckRequestError(
  error: any,
  ignoredLoc: string[] = ["body", "person_info"]
): string[] {
  const detail = error?.response?.data?.detail;
  if (!detail) {
    return [];
  }
  if (typeof detail === "string") {
    return [detail];
  }
  if (!Array.isArray(detail)) {
    return [];
  }
  return detail.map((part: any) => {
    if (typeof part === "string") {
      return part;
    }
    const loc = (part.loc || [])
      .filter((value: any) => !ignoredLoc.includes(value))
      .join(" => ");
    const message = part.msg ?? String(part);
    return loc ? `${loc}: ${message}` : message;
  });
}

export function useGetReportPendingReviewList(
  request: ReportsPendingReviewRequest | null
) {
  const { requestApi } = useAppContext();
  const queryClient = useQueryClient();

  const getPendingReview = useCallback(
    async (
      input: ReportsPendingReviewRequest
    ): Promise<ReportsPendingReviewResponse> => {
      let workflowUrl = "workflow/in_review";
      if (input?.individual === true) workflowUrl += "/individual";
      return requestApi
        .get(`${API_URL}/${workflowUrl}`, input)
        .then((response) => {
          response.data.timestamp = Date.now();
          return response.data;
        });
    },
    [requestApi]
  );

  const queryResult = useQuery(
    [PENDING_REVIEW_QUERY_KEY, request],
    () => (request ? getPendingReview(request) : Promise.resolve(null)),
    {
      enabled: request !== null,
    }
  );

  const refetch = useCallback(() => {
    if (request) {
      queryClient.invalidateQueries([PENDING_REVIEW_QUERY_KEY, request]);
    }
  }, [queryClient, request]);

  return {
    ...queryResult,
    data: queryResult.data ?? undefined,
    mutate: refetch,
    getReportsFetchList: refetch,
  };
}

export function useInvalidateReportPendingReviewList() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.invalidateQueries([PENDING_REVIEW_QUERY_KEY]);
  }, [queryClient]);
}

export function useGetReportReviewList(input: ReportsPendingReviewRequest) {
  const { requestApi } = useAppContext();

  const queryKey = [
    "reportReviewList",
    input?.individual,
    input?.search,
    input?.page,
    input?.page_size,
    input?.company_uuids?.slice().sort().join(",") ?? "",
  ];

  return useQuery<ReportsPendingReviewResponse>(
    queryKey,
    async () => {
      let workflowUrl = "workflow/reviewed";
      if (input?.individual === true) workflowUrl += "/individual";
      const response = await requestApi.get(`${API_URL}/${workflowUrl}`, input);
      response.data.timestamp = Date.now();
      return response.data;
    },
    {
      enabled:
        input?.page_size != null &&
        input?.page != null &&
        Array.isArray(input?.company_uuids),
      staleTime: 30000,
      cacheTime: 5 * 60 * 1000,
      retry: 2,
      keepPreviousData: true,
    }
  );
}

export function usePostReportReviewMark() {
  // deprecated, replaced by useMarkReviewed
  const { requestApi } = useAppContext();

  const reviewMark = async (
    workflow_run_uuids: string[]
  ): Promise<ReportsPendingReviewResponse> => {
    return requestApi
      .post(`${API_URL}/workflow/review`, { workflow_run_uuids })
      .then((response) => {
        response.data.timestamp = Date.now();
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (workflow_run_uuids: string[]) => reviewMark(workflow_run_uuids),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useMarkReviewed() {
  const { requestApi } = useAppContext();

  const markReviewed = async (
    payload: MarkReviewedInput
  ): Promise<ProductReport[]> => {
    return requestApi
      .post(`${API_URL}/productreports/review`, payload)
      .then((response) => response.data);
  };

  return useMutation((payload: MarkReviewedInput) => markReviewed(payload), {});
}

/**
 * Original mutation-based hook for fetching report summary list.
 * Used by ReportsCompleted, ReportsAmendment, and useGetReportInfoListFilter.
 */
export function useGetReportInfoList(
  canViewAllReportsWithinCompany?: boolean,
  enableProductReportsForClientTab?: boolean
) {
  const { requestApi, cache, refreshCache } = useAppContext();
  const { mutateAsync: getActorInfo } = useGetActorInfo();

  const getReportSummary = async (
    input: ReportListInputApi
  ): Promise<ReportSummaryApi> => {
    if (isMocksEnabled()) {
      return await getReportsInfoListMock();
    }
    const currentCache = cache.current.reports?.find(
      (element) =>
        element.page === input.page &&
        element.type === input.workflow_status?.join("-") &&
        element.search === input.person_name
    );
    if (currentCache?.items && !Array.isArray(currentCache?.items)) {
      currentCache.items.timestamp = currentCache.timestamp;
      return currentCache.items;
    }

    if (
      input.workflow_status &&
      input.workflow_status.includes(WORKFLOW_REPORT_STATUS_ENUM.AMENDMENT)
    ) {
      return requestApi
        .get(`${API_URL}/reports/summary/amendment`, input)
        .then((response) => {
          refreshCache(
            CacheKeys.reports,
            response.data,
            input.page,
            input.workflow_status?.join("-"),
            input.person_name
          );
          response.data.timestamp = Date.now();
          return response.data;
        });
    }

    if (
      input.workflow_status &&
      input.workflow_status.includes(WORKFLOW_REPORT_STATUS_ENUM.IN_PROGRESS)
    ) {
      let endpoint = "";
      if (enableProductReportsForClientTab) {
        if (canViewAllReportsWithinCompany)
          endpoint = "/product_reports/misconduct/in_progress/full";
        else endpoint = "/product_reports/misconduct/in_progress/individual";
      } else {
        if (canViewAllReportsWithinCompany)
          endpoint = "/reports/summary/in_progress";
        else endpoint = "/reports/summary/in_progress/individual";
      }
      endpoint = API_URL + endpoint;

      return requestApi
        .get(endpoint, { ...input, include_three_sixty: true })
        .then((response) => {
          refreshCache(
            CacheKeys.reports,
            response.data,
            input.page,
            input.workflow_status?.join("-"),
            input.person_name
          );
          response.data.timestamp = Date.now();
          return response.data;
        });
    }
    if (
      input.workflow_status &&
      input.workflow_status.includes(WORKFLOW_REPORT_STATUS_ENUM.IN_PROCESS)
    ) {
      let endpoint = "/reports/summary/in_process";

      const actorInfo: ActorInfoApi = await getActorInfo();
      if (actorInfo.has_task === true) {
        endpoint = "/reports/summary/in_process/has_task";
      }
      endpoint = API_URL + endpoint;
      return requestApi.get(endpoint, input).then((response) => {
        refreshCache(
          CacheKeys.reports,
          response.data,
          input.page,
          input.workflow_status?.join("-"),
          input.person_name
        );
        response.data.timestamp = Date.now();
        return response.data;
      });
    }
    if (
      input.workflow_status &&
      input.workflow_status.includes(WORKFLOW_REPORT_STATUS_ENUM.INTERNAL_QA)
    ) {
      let endpoint = API_URL + "/reports/summary/internal_qa/in_progress";
      return requestApi.get(endpoint, input).then((response) => {
        refreshCache(
          CacheKeys.reports,
          response.data,
          input.page,
          input.workflow_status?.join("-"),
          input.person_name
        );
        response.data.timestamp = Date.now();
        return response.data;
      });
    }
    if (
      input.workflow_status &&
      input.workflow_status.includes(WORKFLOW_REPORT_STATUS_ENUM.CLIENT_QA)
    ) {
      let endpoint = API_URL + "/reports/summary/client_qa/in_progress";
      return requestApi.get(endpoint, input).then((response) => {
        refreshCache(
          CacheKeys.reports,
          response.data,
          input.page,
          input.workflow_status?.join("-"),
          input.person_name
        );
        response.data.timestamp = Date.now();
        return response.data;
      });
    }

    if (
      input.workflow_status &&
      input.workflow_status.includes(WORKFLOW_REPORT_STATUS_ENUM.COMPLETED)
    ) {
      let endpoint =
        canViewAllReportsWithinCompany === false
          ? "/reports/summary/completed/individual"
          : "/reports/summary/completed";
      endpoint = API_URL + endpoint;

      return requestApi.get(endpoint, input).then((response) => {
        refreshCache(
          CacheKeys.reports,
          response.data,
          input.page,
          input.workflow_status?.join("-"),
          input.person_name
        );
        response.data.timestamp = Date.now();
        return response.data;
      });
    }
    //
    return requestApi
      .get(`${API_URL}/reports/summary`, input)
      .then((response) => {
        refreshCache(
          CacheKeys.reports,
          response.data,
          input.page,
          input.workflow_status?.join("-"),
          input.person_name
        );
        response.data.timestamp = Date.now();
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: ReportListInputApi) => getReportSummary(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

/**
 * Fetches report summary from the API. Used by useGetReportInfoListQuery.
 * For IN_PROCESS status, hasTask determines which endpoint is used.
 */
async function fetchReportSummary(
  requestApi: ReturnType<typeof useAppContext>["requestApi"],
  input: ReportListInputApi,
  canViewAllReportsWithinCompany: boolean | undefined,
  enableProductReportsForClientTab: boolean | undefined,
  hasTask?: boolean
): Promise<ReportSummaryApi> {
  if (isMocksEnabled()) {
    return await getReportsInfoListMock();
  }

  if (
    input.workflow_status &&
    input.workflow_status.includes(WORKFLOW_REPORT_STATUS_ENUM.AMENDMENT)
  ) {
    const response = await requestApi.get(
      `${API_URL}/reports/summary/amendment`,
      input
    );
    response.data.timestamp = Date.now();
    return response.data;
  }

  if (
    input.workflow_status &&
    input.workflow_status.includes(WORKFLOW_REPORT_STATUS_ENUM.IN_PROGRESS)
  ) {
    let endpoint = "";
    if (enableProductReportsForClientTab) {
      if (canViewAllReportsWithinCompany)
        endpoint = "/product_reports/misconduct/in_progress/full";
      else endpoint = "/product_reports/misconduct/in_progress/individual";
    } else {
      if (canViewAllReportsWithinCompany)
        endpoint = "/reports/summary/in_progress";
      else endpoint = "/reports/summary/in_progress/individual";
    }
    const response = await requestApi.get(API_URL + endpoint, {
      ...input,
      include_three_sixty: true,
    });
    response.data.timestamp = Date.now();
    return response.data;
  }

  if (
    input.workflow_status &&
    input.workflow_status.includes(WORKFLOW_REPORT_STATUS_ENUM.IN_PROCESS)
  ) {
    const endpoint = hasTask
      ? `${API_URL}/reports/summary/in_process/has_task`
      : `${API_URL}/reports/summary/in_process`;
    const response = await requestApi.get(endpoint, input);
    response.data.timestamp = Date.now();
    return response.data;
  }

  if (
    input.workflow_status &&
    input.workflow_status.includes(WORKFLOW_REPORT_STATUS_ENUM.INTERNAL_QA)
  ) {
    const response = await requestApi.get(
      `${API_URL}/reports/summary/internal_qa/in_progress`,
      input
    );
    response.data.timestamp = Date.now();
    return response.data;
  }

  if (
    input.workflow_status &&
    input.workflow_status.includes(WORKFLOW_REPORT_STATUS_ENUM.CLIENT_QA)
  ) {
    const response = await requestApi.get(
      `${API_URL}/reports/summary/client_qa/in_progress`,
      input
    );
    response.data.timestamp = Date.now();
    return response.data;
  }

  if (
    input.workflow_status &&
    input.workflow_status.includes(WORKFLOW_REPORT_STATUS_ENUM.COMPLETED)
  ) {
    const response = await requestApi.get(
      `${API_URL}/reports/summary/completed`,
      input
    );
    response.data.timestamp = Date.now();
    return response.data;
  }

  const response = await requestApi.get(`${API_URL}/reports/summary`, input);
  response.data.timestamp = Date.now();
  return response.data;
}

/**
 * React Query-based hook for fetching report summary list (IN_PROGRESS).
 * Same pattern as useGetReportsInProcessQuery. Only consumed by ReportsInProgress.
 */
export function useGetReportInfoListQuery(
  input: ReportListInputApi,
  canViewAllReportsWithinCompany?: boolean,
  enableProductReportsForClientTab?: boolean,
  enabled: boolean = true
) {
  const { requestApi } = useAppContext();
  const needsActorInfo =
    enabled &&
    !!input.workflow_status?.includes(WORKFLOW_REPORT_STATUS_ENUM.IN_PROCESS);
  const { data: actorInfo, isLoading: isLoadingActorInfo } =
    useActorInfoQuery(needsActorInfo);
  const hasTask = actorInfo?.has_task === true;

  return useQuery<ReportSummaryApi>(
    [
      "reportInfoListQuery",
      input?.page,
      input?.page_size,
      input?.person_name,
      input?.workflow_status,
      input?.descending,
      canViewAllReportsWithinCompany,
      enableProductReportsForClientTab,
      hasTask,
    ],
    () =>
      fetchReportSummary(
        requestApi,
        input,
        canViewAllReportsWithinCompany,
        enableProductReportsForClientTab,
        input.workflow_status?.includes(WORKFLOW_REPORT_STATUS_ENUM.IN_PROCESS)
          ? hasTask
          : undefined
      ),
    {
      enabled:
        enabled &&
        (!needsActorInfo || !isLoadingActorInfo) &&
        input?.page_size !== undefined,
      staleTime: 30000,
      cacheTime: 5 * 60 * 1000,
      retry: 2,
      keepPreviousData: true,
    }
  );
}

/**
 * React Query-based hook for fetching Reports In Process.
 * Uses useQuery for automatic caching, deduplication, and background refetching.
 * Calls the existing /reports/summary/in_process endpoints with batch fetching on backend.
 *
 * Benefits over the mutation-based approach:
 * - Automatic request deduplication
 * - Built-in stale-while-revalidate caching
 * - Background refetch on window focus
 * - Simpler component usage (no need to call mutate in useEffect)
 * - Backend uses batch fetching (no N+1 queries)
 */
export function useGetReportsInProcessQuery(
  page: number,
  pageSize: number,
  personName?: string,
  enabled: boolean = true,
  errorsOnly: boolean = false
) {
  const { requestApi } = useAppContext();

  return useQuery<ReportSummaryApi>(
    ["reportsInProcess", page, pageSize, personName, errorsOnly],
    async () => {
      const endpoint = `${API_URL}/reports/summary/in_process`;

      const input: ReportListInputApi = {
        person_name: personName,
        page_size: pageSize,
        page: page - 1,
      };
      if (errorsOnly) {
        input.errors_only = 1;
      }

      const response = await requestApi.get(endpoint, input);
      response.data.timestamp = Date.now();
      return response.data;
    },
    {
      enabled,
      staleTime: 30000,
      cacheTime: 5 * 60 * 1000,
      retry: 2,
      keepPreviousData: true,
    }
  );
}

export function useGetReportsSummarByStepNameList(
  stepName: string | undefined,
  page: number = 1,
  pageSize: number = 10,
  minDueDate?: string,
  maxDueDate?: string,
  days?: number
) {
  const { requestApi } = useAppContext();

  return useQuery(
    [
      "reportsSummarByStepNameList",
      stepName,
      page,
      pageSize,
      minDueDate,
      maxDueDate,
      days,
    ],
    async (): Promise<{
      data: WorkflowByStepSummaryItem[];
      pagination: ReportSummaryPagination;
    }> => {
      let workflowStepNames = uiNameToWorkflowStepNames(stepName || "");

      if (!workflowStepNames || !stepName) {
        throw new Error("Step name is required or invalid step name");
      }

      const params: ReportsByStepNameParams = {
        workflow_step_names: workflowStepNames,
        page: page.toString(),
        page_size: pageSize.toString(),
      };

      if (minDueDate) {
        const isoMinDate = new Date(minDueDate).toISOString();
        params.min_due_date = isoMinDate;
      }
      if (maxDueDate) {
        const isoMaxDate = new Date(maxDueDate).toISOString();
        params.max_due_date = isoMaxDate;
      }
      if (days) {
        params.days = days.toString();
      }

      return requestApi
        .get(`${API_URL}/reports/summary/by_workflow_step_name`, params)
        .then((response) => {
          return response.data;
        });
    },
    {
      enabled: !!stepName && ((!!minDueDate && !!maxDueDate) || !!days),
      refetchOnWindowFocus: "always",
      retry: 2,
    }
  );
}

export function useGetReportInfoListFilter() {
  const { enableProductReportsForClientTab } = useFamaFlags();
  const {
    isLoading: isLoadingList,
    data,
    mutate,
    error,
  } = useGetReportInfoList(false, enableProductReportsForClientTab);
  const [filteredList, setFilteredList] = useState<ReportSummaryItemApi[]>();
  const [fetchInput, setFetchInput] = useState<ReportListInputApi>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const mutateFiltered = useCallback(
    (input: ReportListInputApi) => {
      setFetchInput(input);
      mutate(input);
    },
    [mutate]
  );

  useEffect(() => {
    if (!isLoadingList && data && fetchInput) {
      const listByStatus: ReportSummaryItemApi[] = data.report_summaries;

      const list: ReportSummaryItemApi[] = listByStatus.filter((item) =>
        item.full_name
          .toLocaleLowerCase()
          .includes(fetchInput.person_name ?? "")
      );

      setFilteredList(list);
      setIsLoading(false);
    }
  }, [data, fetchInput, isLoadingList]);

  return { isLoading, data: filteredList, mutate: mutateFiltered, error };
}

export function usePostCreateReport() {
  const [errorData, setErrorData] = useState<string[]>([]);
  const { requestApi, refreshCache } = useAppContext();

  function transformError(error: any): string[] {
    return transformCheckRequestError(error);
  }
  const postCreateReport = async (
    input: CreateReportInputApi
  ): Promise<string> => {
    return requestApi
      .post(`${API_URL}/reports`, {
        ...input,
      })
      .then((response) => {
        refreshCache(CacheKeys.reports);
        return response.data;
      })
      .catch((error) => {
        setErrorData(transformError(error));
        throw new Error(error.response.data.message);
      });
  };

  const mutationResult = useMutation(
    (input: CreateReportInputApi) => postCreateReport(input),
    {}
  );

  return {
    ...mutationResult,
    errorData,
  };
}

export function usePostCreateReportV2() {
  const [errorData, setErrorData] = useState<string[]>([]);
  const { requestApi, refreshCache } = useAppContext();

  function transformError(error: any): string[] {
    return transformCheckRequestError(error);
  }
  const postCreateReportV2 = async (
    input: CreateReportInputV2Api
  ): Promise<string> => {
    return requestApi
      .post(`${API_URL}/reports/v2`, {
        ...input,
      })
      .then((response) => {
        refreshCache(CacheKeys.reports);
        return response.data;
      })
      .catch((error) => {
        setErrorData(transformError(error));
        throw new Error(error.response.data.message);
      });
  };

  const mutationResult = useMutation(
    (input: CreateReportInputV2Api) => postCreateReportV2(input),
    {}
  );

  return {
    ...mutationResult,
    errorData,
  };
}

export function usePostCreateReport2025() {
  const [errorData, setErrorData] = useState<string[]>([]);
  const { requestApi, refreshCache } = useAppContext();

  function transformError(error: any): string[] {
    return transformCheckRequestError(error, ["body", "person_info", "person"]);
  }
  const postCreateReportV2025 = async (
    input: CreateReportInputV2025Api
  ): Promise<string> => {
    const companyUUID = input.company_uuid;
    return requestApi
      .post(`${API_URL}/company/${companyUUID}/check/2025`, {
        ...input,
      })
      .then((response) => {
        refreshCache(CacheKeys.reports);
        return response.data;
      })
      .catch((error) => {
        setErrorData(transformError(error));
        throw new Error(error.response.data.message);
      });
  };

  const mutationResult = useMutation(
    (input: CreateReportInputV2025Api) => postCreateReportV2025(input),
    {}
  );

  return {
    ...mutationResult,
    errorData,
  };
}

export function useCreateReportNote() {
  const { requestApi } = useAppContext();

  const createReportNote = async (
    input: CreateReportNoteRequest
  ): Promise<any> => {
    // if (isMocksEnabled()) {
    // }
    const body = { log_message: input.note };
    return requestApi.post(
      `${API_URL}/reports/logs/${input.report_uuid}?actor_uuid=${input.actor_uuid}`,
      body
    );
  };
  const mutationResult = useMutation(
    (input: CreateReportNoteRequest) => createReportNote(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export type ActiveWorkflowStepRunApi = {
  id: number;
  external_id: string;
  step_name: string;
  payload: Record<string, unknown>;
  ui_link?: string | null;
};

export type LatestWorkflowStepRunApi = ActiveWorkflowStepRunApi & {
  active_steps?: ActiveWorkflowStepRunApi[];
};

export async function fetchLatestWorkflowStepRun(
  requestApi: RequestApi,
  workflowRunUuid: string
): Promise<LatestWorkflowStepRunApi> {
  const response = await requestApi.get(
    `${API_URL}/workflow_runs/${workflowRunUuid}/latest_step_run`
  );
  return response.data;
}

export function useLatestWorkflowStepRunQuery(
  workflowRunUuid: string | undefined,
  enabled: boolean
) {
  const { requestApi } = useAppContext();

  return useQuery(
    ["latestWorkflowStepRun", workflowRunUuid],
    () => fetchLatestWorkflowStepRun(requestApi, workflowRunUuid!),
    {
      enabled: Boolean(workflowRunUuid) && enabled,
      staleTime: 30_000,
      cacheTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );
}

export function useReportByUUID(
  canViewAllReportsWithinCompany?: boolean,
  useParent?: boolean
) {
  const { requestApi } = useAppContext();

  const reportByReportUUID = async (
    reportUUID: string
  ): Promise<ReportSummaryItemApi> => {
    const path =
      canViewAllReportsWithinCompany === false
        ? "/report/summary/individual"
        : "/report/summary";
    let url = `${API_URL}${path}?report_uuid=${reportUUID}`;
    if (useParent) {
      url += "&use_parent=true";
    }
    const response = await requestApi.get(url);
    return response.data;
  };

  const mutationResult = useMutation(
    (reportUUID: string) => reportByReportUUID(reportUUID),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetReportLogs() {
  const { requestApi } = useAppContext();

  const getReportLogsByUUID = async (
    reportUUID: string
  ): Promise<ReportLog[]> => {
    if (isMocksEnabled()) {
      return await getReportLogsMock(10);
    }

    return requestApi
      .get(
        `${API_URL}/report_logs_multifilter?report_uuid=${reportUUID}&log_type=system`
      )
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (reportUUID: string) => getReportLogsByUUID(reportUUID),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useCreateImage() {
  const { requestApi } = useAppContext();

  function createImage(input: CreateImageRequest): Promise<string | undefined> {
    return requestApi
      .post(`${API_URL}/image/upload`, input)
      .then((response) => {
        if (response.data) {
          return response.data;
        }
      });
  }

  const mutationResult = useMutation(
    (input: CreateImageRequest) => createImage(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetImagePresignedUrl() {
  const { requestApi } = useAppContext();

  const getImagePresignedUrl = async (
    input: GetImagePresignedUrlRequest
  ): Promise<string> => {
    return requestApi
      .post(`${API_URL}/image/upload/s3_presigned_url`, input)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: GetImagePresignedUrlRequest) => getImagePresignedUrl(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useUploadImageToS3() {
  const uploadFileToS3 = async ({
    uploadUrl,
    file,
  }: {
    uploadUrl: string;
    file: File;
  }): Promise<void> => {
    const res = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type || "application/octet-stream" },
    });
    if (!res.ok) throw new Error("Failed to upload file to S3");
  };

  const mutationResult = useMutation(uploadFileToS3, {});

  return {
    ...mutationResult,
  };
}

export function useGetReportsNotes() {
  const { requestApi } = useAppContext();

  const getReportLogsByUUID = async (
    reportUUID: string
  ): Promise<ReportLog[]> => {
    if (isMocksEnabled()) {
      return await getReportLogsMock(10);
    }

    return requestApi
      .get(
        `${API_URL}/report_logs_multifilter?report_uuid=${reportUUID}&log_type=user`
      )
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (reportUUID: string) => getReportLogsByUUID(reportUUID),
    {}
  );

  return {
    ...mutationResult,
  };
}

/**
 * WorkflowStepRuns.name values for steps configured as sub-workflow triggers (domain "workflow"
 * in fama_interfaces/workflow_dal.py WORKFLOW_STEP_DOMAINS), including report-completion kickoffs
 * Misconduct and CompleteReport (DEV-4605). Only runs whose steps_by_run payload includes at
 * least one of these names will trigger a GET …/children call.
 */
export const WORKFLOW_DEBUG_SUBWORKFLOW_TRIGGER_STEP_NAMES: ReadonlySet<string> =
  new Set([
    "InstantFit",
    "Misconduct",
    "CompleteReport",
    "PostProcessing",
    "ScrapeProcess",
    "QA",
    "360",
    "360WebSearch",
  ]);

function stepsSuggestSubworkflowChildren(steps: WorkflowStepRun[]): boolean {
  return steps.some((s) =>
    WORKFLOW_DEBUG_SUBWORKFLOW_TRIGGER_STEP_NAMES.has(s.name)
  );
}

/**
 * Loads step runs for the root workflow run and every descendant subworkflow (BFS via
 * GET /workflow_runs/{uuid}/children when warranted), merging results. steps_by_run only
 * returns one run (plus its parent); child runs must be fetched separately.
 */
export async function fetchAllWorkflowStepRunsForDebug(
  requestApi: RequestApi,
  rootWorkflowRunUuid: string
): Promise<WorkflowStepRun[]> {
  const byId = new Map<number, WorkflowStepRun>();
  const processedRuns = new Set<string>();
  const queue: string[] = [String(rootWorkflowRunUuid)];

  while (queue.length > 0) {
    const u = queue.shift()!;
    if (processedRuns.has(u)) {
      continue;
    }
    processedRuns.add(u);

    const stepsRes = await requestApi.get(
      `${API_URL}/workflows/steps_by_run?workflow_run_uuid=${u}`
    );
    const stepsForRun = stepsRes.data as WorkflowStepRun[];
    for (const s of stepsForRun) {
      if (!byId.has(s.id)) {
        byId.set(s.id, s);
      }
    }

    if (!stepsSuggestSubworkflowChildren(stepsForRun)) {
      continue;
    }

    const childrenRes = await requestApi.get(
      `${API_URL}/workflow_runs/${u}/children`
    );
    for (const c of childrenRes.data as WorkflowRun[]) {
      if (c.external_id == null) {
        continue;
      }
      const cid = String(c.external_id);
      if (!processedRuns.has(cid)) {
        queue.push(cid);
      }
    }
  }

  return Array.from(byId.values());
}

export function useGetStepRunsByRunUUID() {
  const { requestApi } = useAppContext();

  const getWorkflowStepRunsByRunUUID = async (
    workflow_run_uuid: string
  ): Promise<WorkflowStepRun[]> => {
    return requestApi
      .get(
        `${API_URL}/workflows/steps_by_run?workflow_run_uuid=${workflow_run_uuid}`
      )
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (workflowRunUUID: string) => getWorkflowStepRunsByRunUUID(workflowRunUUID),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useDownloadReportPDF(pdf_type?: string) {
  const { requestApi } = useAppContext();

  const downloadReportPDF = async (
    reportUUID: string
  ): Promise<string | void> => {
    if (pdf_type !== "company") {
      pdf_type = "reports";
    }
    return requestApi
      .get(`${API_URL}/${pdf_type}/pdf/${reportUUID}`)
      .then(async (response: any) => {
        if (response.status === 200) {
          const downloadUrl = response.data.report_url;
          resolveAndDownload(downloadUrl);
        } else {
          let json = await response.json();
          throw new Error(`Error downloading PDF: ${json.message}`);
        }
      });
  };

  const mutationResult = useMutation(
    (reportUUID: string) => downloadReportPDF(reportUUID),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useReportAmendment() {
  const { requestApi, refreshCache } = useAppContext();

  const reportAmendment = async (reportUUID: string): Promise<ReportLog[]> => {
    return requestApi
      .patch(`${API_URL}/amendment/start/${reportUUID}`)
      .then((response) => {
        refreshCache(CacheKeys.reports);
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (reportUUID: string) => reportAmendment(reportUUID),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useReportCancel() {
  const { requestApi, refreshCache } = useAppContext();

  const reportCancel = async (reportUUID: string): Promise<ReportLog[]> => {
    return requestApi
      .post(`${API_URL}/report/cancel/${reportUUID}`)
      .then((response) => {
        refreshCache(CacheKeys.reports);
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (reportUUID: string) => reportCancel(reportUUID),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useReportDelete() {
  const { requestApi, refreshCache } = useAppContext();

  const reportDelete = async (reportUUID: string): Promise<ReportLog[]> => {
    return requestApi
      .delete(`${API_URL}/report/delete/${reportUUID}`)
      .then((response) => {
        refreshCache(CacheKeys.reports);
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (reportUUID: string) => reportDelete(reportUUID),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetReportAdminCompletedList() {
  const { requestApi } = useAppContext();

  const getCompletedList = async (
    input: ReportsAdminCompletedInput
  ): Promise<ReportSummaryApi> => {
    let workflowUrl = "reports/admin/completed/all";

    return requestApi
      .post(
        `${API_URL}/${workflowUrl}?page=${input.page}&page_size=${input.page_size}`,
        input.filter
      )
      .then((response) => {
        response.data.timestamp = Date.now();
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: ReportsAdminCompletedInput) => getCompletedList(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

function buildOperationParams(input: {
  [key: string]: string[] | string | undefined;
  page?: string;
  page_size?: string;
}) {
  const params: {
    min_due_date?: string;
    max_due_date?: string;
    workflow_step_name?: string;
    workflow_step_name_csv?: string;
  } = {};

  if (input.min_due_date && typeof input.min_due_date === "string") {
    params.min_due_date = input.min_due_date;
  }
  if (input.max_due_date && typeof input.max_due_date === "string") {
    params.max_due_date = input.max_due_date;
  }
  if (input.workflow_step_name) {
    params.workflow_step_name = Array.isArray(input.workflow_step_name)
      ? input.workflow_step_name.join(",")
      : input.workflow_step_name;
  }
  if (input.workflow_step_name_csv) {
    params.workflow_step_name_csv = input.workflow_step_name_csv as string;
  }

  return new URLSearchParams(params);
}

export function useGetReportAdminOperationList() {
  const { requestApi } = useAppContext();

  const getCompletedList = async (
    input: ReportsAdminOperationsInput
  ): Promise<ReportSummaryApi> => {
    let workflowUrl = "reports/admin/operations/all";
    const filterCopy = structuredClone(input.filter);
    const params = filterCopy
      ? buildOperationParams({
          ...filterCopy,
          page: input.page?.toString() ?? "1",
          page_size: input.page_size?.toString() ?? "10",
        })
      : new URLSearchParams();
    Object.keys(filterCopy ?? {}).forEach((key) => {
      if (
        input &&
        (filterCopy?.[key as keyof typeof filterCopy]?.length ?? 0) === 0
      ) {
        delete filterCopy?.[key as keyof typeof filterCopy];
      }
    });

    if (filterCopy?.workflow_step_name_csv) {
      delete filterCopy?.workflow_step_name_csv;
    }

    return requestApi
      .post(
        `${API_URL}/${workflowUrl}?page=${input.page}&page_size=${
          input.page_size
        }&${params.toString()}`,
        { ...filterCopy, min_due_date: undefined, max_due_date: undefined }
      )
      .then((response) => {
        response.data.timestamp = Date.now();
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: ReportsAdminOperationsInput) => getCompletedList(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetReportAdminOperationFilter() {
  const { requestApi } = useAppContext();

  const getFilterData = async (input: {
    [key: string]: string[] | string;
  }): Promise<FilterOptions> => {
    let workflowUrl = "reports/admin/operations/filter";
    const inputCopy = structuredClone(input);
    const qs = buildOperationParams(inputCopy);
    if (inputCopy?.workflow_step_name_csv) {
      delete inputCopy.workflow_step_name_csv;
    }

    return requestApi
      .post(`${API_URL}/${workflowUrl}?${qs.toString()}`, {
        ...inputCopy,
        min_due_date: undefined,
        max_due_date: undefined,
      })
      .then((response) => {
        const data = response.data;

        const sorted_dict: {
          [key: string]: string | { key: string; value: string }[];
        } = {};

        Object.keys(data).forEach((key: any) => {
          const sortedData: string | { key: string; value: string }[] = data[
            key
          ].sort(
            (a: string | { value: string }, b: string | { value: string }) => {
              if (typeof a === "string" && typeof b === "string") {
                return a.localeCompare(b);
              } else if (typeof a === "object" && typeof b === "object") {
                return a.value.localeCompare(b.value);
              }
              return 0;
            }
          );
          sorted_dict[key] = sortedData;
        });

        return sorted_dict as unknown as FilterOptions;
      });
  };

  const mutationResult = useMutation(
    (input: { [key: string]: string[] | string }) => getFilterData(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetCompletedFitlerList() {
  const { requestApi } = useAppContext();
  const getFitlerList = async (input: {
    [key: string]: string[];
  }): Promise<FilterOptions> => {
    return requestApi
      .post(`${API_URL}/reports/admin/completed/filter`, input)
      .then((response) => {
        const data = response.data;
        if (data["company_uuid"]) {
          data["company_uuid"].filter((c: KeyValue) =>
            data["company_uuid"].includes(c.value)
          );
        }

        const sorted_dict: {
          [key: string]: string | { key: string; value: string }[];
        } = {};

        Object.keys(data).forEach((key: any) => {
          const sortedData: string | { key: string; value: string }[] = data[
            key
          ].sort(
            (a: string | { value: string }, b: string | { value: string }) => {
              if (typeof a === "string" && typeof b === "string") {
                return a.localeCompare(b);
              } else if (typeof a === "object" && typeof b === "object") {
                return a.value.localeCompare(b.value);
              }
              return 0;
            }
          );
          sorted_dict[key] = sortedData;
        });

        return sorted_dict as unknown as FilterOptions;
      });
  };

  const mutationResult = useMutation(
    (input: { [key: string]: string[] }) => getFitlerList(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useRegeneratePDF() {
  const { requestApi, refreshCache } = useAppContext();

  const regeneratePDF = async (reportUUID: string): Promise<ReportLog[]> => {
    return requestApi
      .post(
        `${API_URL}/reports/trigger_pdf_generation?report_uuid=${reportUUID}`
      )
      .then((response) => {
        refreshCache(CacheKeys.reports);
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (reportUUID: string) => regeneratePDF(reportUUID),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useCheckPDF() {
  const { requestApi } = useAppContext();

  const checkPDF = async (
    reportUUID: string
  ): Promise<{ report_uuid: string; exists: boolean }> => {
    return requestApi
      .get(`${API_URL}/reports/checkpdf/${reportUUID}`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (reportUUID: string) => checkPDF(reportUUID),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useFetchReportRerun() {
  const { requestApi } = useAppContext();

  const fetchReportRerun = async (
    reportUUID: string
  ): Promise<ReportRerunSummary[]> => {
    return requestApi
      .get(`${API_URL}/reports/reruns/${reportUUID}`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (reportUUID: string) => fetchReportRerun(reportUUID),
    {}
  );

  return {
    ...mutationResult,
  };
}

/**
 * Fetches rerun history for a report and clears stale mutation data on navigation.
 */
export function useReportReruns(reportUuid: string | undefined) {
  const { data, mutate, reset, variables, isLoading, isError, error } =
    useFetchReportRerun();

  useEffect(() => {
    if (!reportUuid) {
      reset();
      return;
    }
    reset();
    mutate(reportUuid);
  }, [reportUuid, mutate, reset]);

  const reportReruns = useMemo(
    () =>
      getAlignedReportReruns(
        reportUuid,
        variables as string | undefined,
        isLoading,
        data
      ),
    [reportUuid, variables, isLoading, data]
  );

  return {
    reportReruns,
    isLoading,
    isError,
    error,
  };
}

export interface RerunReportRequestBody {
  blueprint_uuid: string;
  /** Explicit null means General / no kit (must be sent when `includeBehaviorKitInPayload` is true). */
  behavior_kit_uuid?: string | null;
  /**
   * Final social_media_profile URL list for the new child report (parent's profiles plus
   * user-added minus user-removed, as edited in the rerun form). When omitted, the backend
   * inherits the parent's profiles verbatim. When provided, this list wins.
   */
  profile_urls?: string[];
  /**
   * Edited subject fields for the rerun. Server always creates a new person record.
   * Optional `person_uuid` on this object is staging-only — see `docs/HYBRID_RERUN.md` and
   * `buildHybridRerunPersonPayload`.
   */
  person?: PersonInfoApi;
  /**
   * Client Reference ID for the new child report. Omit to inherit parent; send `""` to clear.
   * Build via `buildHybridRerunReportBody` — `customId` may be null from API prefill.
   */
  custom_id?: string;
}

/** Prefill only: seed parent in_report URLs plus any URLs the user typed before fetch completed. */
export function mergeHybridRerunProfileUrls(
  profileItems: ItemWithMetaData[] | undefined,
  userUrls: string[]
): string[] | undefined {
  const parentUrls = (profileItems ?? [])
    .filter((entry) => entry.item.in_report)
    .map((entry) => String(entry.item.content?.url ?? "").trim())
    .filter((url) => url.length > 0);

  const extraUserUrls = userUrls
    .map((url) => url.trim())
    .filter((url) => url.length > 0 && !parentUrls.includes(url));

  if (parentUrls.length === 0 && extraUserUrls.length === 0) {
    return undefined;
  }

  return [...parentUrls, ...extraUserUrls];
}

/** Build PATCH body for hybrid rerun. See `docs/HYBRID_RERUN.md`. */
export function buildHybridRerunReportBody(input: {
  blueprintUuid: string;
  behaviorKitUuid?: string;
  includeBehaviorKitInPayload: boolean;
  profileUrls?: string[];
  person?: PersonInfoApi;
  customId?: string | null;
}): RerunReportRequestBody {
  const body: RerunReportRequestBody = { blueprint_uuid: input.blueprintUuid };
  if (input.customId !== undefined) {
    body.custom_id = (input.customId ?? "").trim();
  }
  if (input.profileUrls !== undefined) {
    body.profile_urls = input.profileUrls
      .map((u) => u.trim())
      .filter((u) => u.length > 0);
  }
  if (input.person !== undefined) {
    body.person = input.person;
  }
  if (!input.includeBehaviorKitInPayload) {
    return body;
  }
  const raw = input.behaviorKitUuid ?? "";
  if (raw === "" || raw === "NULL") {
    body.behavior_kit_uuid = null;
  } else {
    body.behavior_kit_uuid = raw;
  }
  return body;
}

export interface RerunReportRequestInput {
  reportUUID: string;
  body?: RerunReportRequestBody;
}

export interface RerunReportRequestResponse {
  message: string;
  rerun_mode: "delta" | "fresh";
  hybrid_rerun_feature_enabled: boolean;
  new_report_uuid?: string;
  new_workflow_run_uuid?: string | null;
}

/** PATCH /report/rerun/{reportUUID}; body should include `blueprint_uuid` (and behavior kit when applicable). */
export function useRerunReportRequest() {
  const { requestApi, refreshCache } = useAppContext();

  const rerunReportRequest = async (
    input: RerunReportRequestInput
  ): Promise<RerunReportRequestResponse> => {
    return requestApi
      .patch(`${API_URL}/report/rerun/${input.reportUUID}`, input.body ?? {})
      .then((response) => {
        refreshCache(CacheKeys.reports);
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: RerunReportRequestInput) => rerunReportRequest(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useFetchLegacyReport() {
  const { requestApi } = useAppContext();

  const fetchLegacyReport = async (
    input: LegacyReportRequest
  ): Promise<LegacyReportResponse> => {
    return requestApi
      .get(`${API_URL}/legacy/reports`, input)
      .then((response) => {
        return { ...response.data, timestamp: Date.now() };
      });
  };

  const mutationResult = useMutation(
    (input: LegacyReportRequest) => fetchLegacyReport(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export interface SearchLegacyReportsParams {
  company_uuid: string;
  search: string;
  limit?: number;
  offset?: number;
}

export function useSearchLegacyReports() {
  const { requestApi } = useAppContext();

  const searchLegacyReports = async (
    params: SearchLegacyReportsParams
  ): Promise<LegacySearchResponse> => {
    const { company_uuid, search, limit = 20, offset = 0 } = params;
    const query = new URLSearchParams({
      company_uuid,
      search,
      limit: limit.toString(),
      offset: offset.toString(),
    }).toString();
    return requestApi
      .get(`${API_URL}/legacy/reports/search?${query}`)
      .then((response) => response.data);
  };

  const mutationResult = useMutation(
    (params: SearchLegacyReportsParams) => searchLegacyReports(params),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useFetchNextTask() {
  const { requestApi } = useAppContext();

  const fetchReportRerun = async (): Promise<string> => {
    return requestApi.get(`${API_URL}/next_task`).then((response) => {
      return response.data;
    });
  };

  const mutationResult = useMutation(() => fetchReportRerun(), {});

  return {
    ...mutationResult,
  };
}

export function useGetReportProductReports() {
  const { requestApi } = useAppContext();
  const getReportProductReports = async (
    report_uuid: string
  ): Promise<ProductReport[]> => {
    if (isMocksEnabled()) {
      return getReportProductReportsMock();
    }
    return requestApi
      .get(`${API_URL}/reports/${report_uuid}/productreports`)
      .then((response) => {
        return response.data;
      });
  };
  const mutationResult = useMutation(
    (report_uuid: string) => getReportProductReports(report_uuid),
    {}
  );
  return { ...mutationResult };
}

export interface CreateReportLogWithTypeRequest {
  report_uuid: string;
  log_message: string;
  type: string;
}

export function useCreateReportTimer() {
  const { requestApi } = useAppContext();
  const createActorTimer = async (
    input: CreateActorTimerRequest
  ): Promise<ReportLog> => {
    return requestApi
      .post(`${API_URL}/actor_timer/create`, {
        name: input.name,
        value: input.value,
        workflow_run_uuid: input.workflow_run_uuid,
        finished_at: input.finished_at,
        status: input.status,
      })
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: CreateActorTimerRequest) => createActorTimer(input),
    {}
  );

  return {
    ...mutationResult,
  };
}
